export interface FishSpecies {
  id: string;
  local_name: string;
  english_name: string;
  aliases?: string[];
  scientific_name?: string;
  image_url: string;
  portion_types?: string[];
  recipes?: string[];
  has_soft_bone?: boolean;

  // Fallback nutrients in case CSV loading fails
  nutrients_per_100g_fallback: {
    [key: string]: {
      value: number;
      unit: "g" | "mg" | "µg" | "µg RAE" | "kcal" | "kj";
    };
  };
}

// data/fish_db.ts
// Updated based on official nutrient database v4 for beta version

export const fishDatabase: FishSpecies[] = [

// ========== FRESHWATER FISH (1-17) ==========

// 1. Bheda (Gangetic leaffish)
{
  id: "bheda",
  local_name: "ভেদা",
  english_name: "Gangetic leaffish",
  aliases: ["Bheda", "Meni", "Royna", "Nonda", "ভেদা", "মেনি", "রয়না", "নন্দা"],
  scientific_name: "Nandus nandus",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/bheda.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.1, unit: "g" },
    fat: { value: 2.96, unit: "g" },
    energy: { value: 718.59, unit: "kj" },
    calcium: { value: 2.57, unit: "mg" },
    iron: { value: 6.42, unit: "mg" },
    zinc: { value: 11.93, unit: "mg" },
    vitaminA: { value: 60, unit: "µg RAE" },
    vitaminD: { value: 0.78, unit: "µg" },
    omega3: { value: 219.06, unit: "mg" },
  },
},

// 2. Catla (Katla)
{
  id: "katla",
  local_name: "কাতলা",
  english_name: "Catla",
  aliases: ["Catla", "Katal", "কাতলা", "কাতল"],
  scientific_name: "Gibelion catla",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/katla.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.68, unit: "g" },
    fat: { value: 0.75, unit: "g" },
    energy: { value: 465.66, unit: "kj" },
    calcium: { value: 0.51, unit: "mg" },
    iron: { value: 5.61, unit: "mg" },
    zinc: { value: 9.37, unit: "mg" },
    vitaminA: { value: 22, unit: "µg RAE" },
    vitaminD: { value: 0.28, unit: "µg" },
    omega3: { value: 94.48, unit: "mg" },
  },
},

// 3. Common Carp (Carpio)
{
  id: "carpio",
  local_name: "কার্পিও",
  english_name: "Common carp",
  aliases: ["Carfu", "Carpio", "কার্ফু", "কার্পিও"],
  scientific_name: "Cyprinus carpio",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/carpio.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.28, unit: "g" },
    fat: { value: 1.24, unit: "g" },
    energy: { value: 821.81, unit: "kj" },
    calcium: { value: 0.77, unit: "mg" },
    iron: { value: 10.32, unit: "mg" },
    zinc: { value: 14.45, unit: "mg" },
    vitaminA: { value: 2, unit: "µg RAE" },
    vitaminD: { value: 6.6, unit: "µg" },
    omega3: { value: 68.85, unit: "mg" },
  },
},

// 4. Koi (Climbing perch)
{
  id: "koi",
  local_name: "কৈ",
  english_name: "Climbing perch",
  aliases: ["Koi", "কৈ"],
  scientific_name: "Anabas testudineus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/koi.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.98, unit: "g" },
    fat: { value: 9.76, unit: "g" },
    energy: { value: 736.71, unit: "kj" },
    calcium: { value: 11.28, unit: "mg" },
    iron: { value: 16.64, unit: "mg" },
    zinc: { value: 9.24, unit: "mg" },
    vitaminA: { value: 295, unit: "µg RAE" },
    vitaminD: { value: 1.19, unit: "µg" },
    omega3: { value: 392.16, unit: "mg" },
  },
},

// 5. Mola
{
  id: "mola",
  local_name: "মলা",
  english_name: "Mola carplet",
  aliases: ["Mola", "Molongi", "Moya", "মলা", "মলঙ্গি", "ময়া"],
  scientific_name: "Amblypharyngodon mola",
  portion_types: ["portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/mola.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.67, unit: "g" },
    fat: { value: 4.4, unit: "g" },
    energy: { value: 583.5, unit: "kj" },
    calcium: { value: 9.87, unit: "mg" },
    iron: { value: 19.39, unit: "mg" },
    zinc: { value: 28.39, unit: "mg" },
    vitaminA: { value: 2503, unit: "µg RAE" },
    vitaminD: { value: 2.03, unit: "µg" },
    omega3: { value: 520.58, unit: "mg" },
  },
},

// 6. Mrigal
{
  id: "mrigel",
  local_name: "মৃগেল",
  english_name: "Mrigal carp",
  aliases: ["Mrigal", "Mirka", "মৃগেল", "মিরকা"],
  scientific_name: "Cirrhinus cirrhosus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/mrigel.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.04, unit: "g" },
    fat: { value: 1.02, unit: "g" },
    energy: { value: 459.9, unit: "kj" },
    calcium: { value: 0.93, unit: "mg" },
    iron: { value: 6.68, unit: "mg" },
    zinc: { value: 6.61, unit: "mg" },
    vitaminA: { value: 15, unit: "µg RAE" },
    vitaminD: { value: 0.616, unit: "µg" },
    omega3: { value: 117.94, unit: "mg" },
  },
},

// 7. Pangus (Thai Pangas)
{
  id: "pangas",
  local_name: "পাঙ্গাস",
  english_name: "Thai pangus",
  aliases: ["Pangus", "পাঙ্গাস"],
  scientific_name: "Pangasius hypopthalmus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/pangas.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.39, unit: "g" },
    fat: { value: 2.35, unit: "g" },
    energy: { value: 802.64, unit: "kj" },
    calcium: { value: 1.38, unit: "mg" },
    iron: { value: 14.65, unit: "mg" },
    zinc: { value: 5.97, unit: "mg" },
    vitaminA: { value: 5, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 144.39, unit: "mg" },
  },
},

// 8. Pora Bele (Dusky sleeper)
{
  id: "pora_bele",
  local_name: "পোরা বেলে",
  english_name: "Dusky sleeper",
  aliases: ["Pora Bele", "Kuli", "Budh Bailla", "পোরা বেলে", "কুলি", "ভূত বাইল্লা"],
  scientific_name: "Eleotris fusca",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: true,
  image_url: "/assets/fish/bele.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.76, unit: "g" },
    fat: { value: 0.78, unit: "g" },
    energy: { value: 439.74, unit: "kj" },
    calcium: { value: 4.44, unit: "mg" },
    iron: { value: 4.6, unit: "mg" },
    zinc: { value: 10.32, unit: "mg" },
    vitaminA: { value: 37, unit: "µg RAE" },
    vitaminD: { value: 22, unit: "µg" },
    omega3: { value: 70.21, unit: "mg" },
  },
},

// 9. Puti (Pool barb)
{
  id: "puti",
  local_name: "পুটি",
  english_name: "Pool barb",
  aliases: ["Puti", "Jat Punti", "Vadipunti", "পুঁটি", "জাতপুঁটি", "ভেদিপুঁটি"],
  scientific_name: "Puntius sophore",
  portion_types: ["whole", "piece", "portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/puti.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.07, unit: "g" },
    fat: { value: 1.9, unit: "g" },
    energy: { value: 525.05, unit: "kj" },
    calcium: { value: 12.86, unit: "mg" },
    iron: { value: 38.6, unit: "mg" },
    zinc: { value: 32.48, unit: "mg" },
    vitaminA: { value: 54, unit: "µg RAE" },
    vitaminD: { value: 1.29, unit: "µg" },
    omega3: { value: 180.92, unit: "mg" },
  },
},

// 10. Rohu (Rui)
{
  id: "rui",
  local_name: "রুই",
  english_name: "Rohou labeo",
  aliases: ["Rui", "Rohit", "Rou", "রুই", "রুহিত", "রউ"],
  scientific_name: "Labeo rohita",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/rui.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.87, unit: "g" },
    fat: { value: 2.24, unit: "g" },
    energy: { value: 507.17, unit: "kj" },
    calcium: { value: 0.54, unit: "mg" },
    iron: { value: 4.31, unit: "mg" },
    zinc: { value: 5.89, unit: "mg" },
    vitaminA: { value: 13, unit: "µg RAE" },
    vitaminD: { value: 1.17, unit: "µg" },
    omega3: { value: 286.19, unit: "mg" },
  },
},

// 11. Shing (Stinging catfish)
{
  id: "shing",
  local_name: "শিং",
  english_name: "Stinging catfish",
  aliases: ["Shing", "Shingi", "শিং", "শিঙ্গি"],
  scientific_name: "Heteropneustes fossilis",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry"],
  has_soft_bone: true,
  image_url: "/assets/fish/shing.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.93, unit: "g" },
    fat: { value: 1.87, unit: "g" },
    energy: { value: 827.71, unit: "kj" },
    calcium: { value: 0.34, unit: "mg" },
    iron: { value: 12.3, unit: "mg" },
    zinc: { value: 7.45, unit: "mg" },
    vitaminA: { value: 32, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 84.23, unit: "mg" },
  },
},

// 12. Shoal (Striped snakehead)
{
  id: "shoal",
  local_name: "শোল",
  english_name: "Striped snakehead",
  aliases: ["Shoal", "শোল"],
  scientific_name: "Channa striatus",
  portion_types: ["whole", "piece", "portion"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/shoal.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.46, unit: "g" },
    fat: { value: 0.63, unit: "g" },
    energy: { value: 466.15, unit: "kj" },
    calcium: { value: 0.30, unit: "mg" },
    iron: { value: 4.28, unit: "mg" },
    zinc: { value: 6.3, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 0.18, unit: "µg" },
    omega3: { value: 61.53, unit: "mg" },
  },
},

// 13. Silver Carp
{
  id: "silver_carp",
  local_name: "সিলভার কার্প",
  english_name: "Silver Carp",
  aliases: ["Silver Carp", "সিলভার কার্প"],
  scientific_name: "Hypophthalmichthys molitrix",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/silver_carp.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.37, unit: "g" },
    fat: { value: 1.09, unit: "g" },
    energy: { value: 742.55, unit: "kj" },
    calcium: { value: 1.80, unit: "mg" },
    iron: { value: 4.07, unit: "mg" },
    zinc: { value: 6.93, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 0.24, unit: "µg" },
    omega3: { value: 101.36, unit: "mg" },
  },
},

// 14. Taki (Spotted snakehead)
{
  id: "taki",
  local_name: "টাকি",
  english_name: "Spotted snakehead",
  aliases: ["Taki", "Lati", "Lata", "Okol", "Chaitan", "টাকি", "লাটি", "লাটা", "ওকল", "চৈতন"],
  scientific_name: "Channa punctatus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/taki.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.58, unit: "g" },
    fat: { value: 1, unit: "g" },
    energy: { value: 674.53, unit: "kj" },
    calcium: { value: 1.01, unit: "mg" },
    iron: { value: 3.55, unit: "mg" },
    zinc: { value: 5.75, unit: "mg" },
    vitaminA: { value: 139, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 94, unit: "mg" },
  },
},

// 15. Tengra (Tengra mystus)
{
  id: "tengra",
  local_name: "টেংরা",
  english_name: "Tengra mystus",
  aliases: ["Tengra", "Bajari-tengra", "Ghuitta-tengra", "টেংরা", "বাজারী টেংরা", "ঘুইত্তা টেংরা"],
  scientific_name: "Mystus tengra",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/tengra.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 15.69, unit: "g" },
    fat: { value: 5.27, unit: "g" },
    energy: { value: 518.75, unit: "kj" },
    calcium: { value: 3.59, unit: "mg" },
    iron: { value: 14.75, unit: "mg" },
    zinc: { value: 12.43, unit: "mg" },
    vitaminA: { value: 43, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 217.99, unit: "mg" },
  },
},

// 16. Thai Sarputi (Silver barb)
{
  id: "sarpunti",
  local_name: "সরপুটি",
  english_name: "Silver barb",
  aliases: ["Thai Sarpunti", "Sarpunti", "থাইসরপুঁটি", "সরপুঁটি"],
  scientific_name: "Barbonymus gonionotus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: true,
  image_url: "/assets/fish/sarpunti.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.1, unit: "g" },
    fat: { value: 2.86, unit: "g" },
    energy: { value: 532.71, unit: "kj" },
    calcium: { value: 0.65, unit: "mg" },
    iron: { value: 5.28, unit: "mg" },
    zinc: { value: 3.99, unit: "mg" },
    vitaminA: { value: 12, unit: "µg RAE" },
    vitaminD: { value: 23, unit: "µg" },
    omega3: { value: 195.95, unit: "mg" },
  },
},

// 17. Tilapia (Mozambique tilapia)
{
  id: "tilapia",
  local_name: "তেলাপিয়া",
  english_name: "Mozambique tilapia",
  aliases: ["Tilapia", "তেলাপিয়া"],
  scientific_name: "Oreochromis mossambicus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/tilapia.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.71, unit: "g" },
    fat: { value: 2.17, unit: "g" },
    energy: { value: 873.1, unit: "kj" },
    calcium: { value: 0.27, unit: "mg" },
    iron: { value: 4.14, unit: "mg" },
    zinc: { value: 4.82, unit: "mg" },
    vitaminA: { value: 2, unit: "µg RAE" },
    vitaminD: { value: 5.5, unit: "µg" },
    omega3: { value: 229.83, unit: "mg" },
  },
},

// ========== MARINE/BRACKISH FISH (18-33) ==========

// 18. Bhangan (Flathead grey mullet)
{
  id: "bhangan",
  local_name: "ভাংগন",
  english_name: "Flathead grey mullet",
  aliases: ["Bhangan", "Bhangan Bata", "Kharul Bata", "ভাংগন", "ভাংগন বাটা", "খরুল বাটা"],
  scientific_name: "Mugil cephalus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/bhangan.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.27, unit: "g" },
    fat: { value: 2.89, unit: "g" },
    energy: { value: 583.71, unit: "kj" },
    calcium: { value: 0.24, unit: "mg" },
    iron: { value: 13.85, unit: "mg" },
    zinc: { value: 5.15, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 509.5, unit: "mg" },
  },
},

// 19. Bhola/Poa/Poma (Pama croaker)
{
  id: "bhola",
  local_name: "পোয়া",
  english_name: "Pama croaker",
  aliases: ["Poa", "Poma", "Bhola", "পোয়া", "পোমা", "ভোলা"],
  scientific_name: "Otolithoides pama",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/bhola.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 11.3, unit: "g" },
    fat: { value: 0.7, unit: "g" },
    energy: { value: 299.88, unit: "kj" },
    calcium: { value: 4.67, unit: "mg" },
    iron: { value: 6.83, unit: "mg" },
    zinc: { value: 6.46, unit: "mg" },
    vitaminA: { value: 17, unit: "µg RAE" },
    vitaminD: { value: 0.6, unit: "µg" },
    omega3: { value: 82.25, unit: "mg" },
  },
},

// 20. Caine Magur (Gray eel-catfish)
{
  id: "caine_magur",
  local_name: "কাইন মাগুর",
  english_name: "Gray eel-catfish",
  aliases: ["Caine Magur", "Gang magur", "কাইন মাগুর", "গাঙ মাগুর"],
  scientific_name: "Plotosus canius",
  portion_types: ["whole", "piece", "portion"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/magur.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.95, unit: "g" },
    fat: { value: 6.62, unit: "g" },
    energy: { value: 617.77, unit: "kj" },
    calcium: { value: 0.26, unit: "mg" },
    iron: { value: 7.09, unit: "mg" },
    zinc: { value: 7.17, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 487.08, unit: "mg" },
  },
},

// 21. Datina (Bluecheek silver grunt)
{
  id: "datina",
  local_name: "দাতিনা",
  english_name: "Bluecheek silver grunt",
  aliases: ["Datina", "দাতিনা"],
  scientific_name: "Pomadasys argyreus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/datina.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.48, unit: "g" },
    fat: { value: 4.79, unit: "g" },
    energy: { value: 444.9, unit: "kj" },
    calcium: { value: 1.01, unit: "mg" },
    iron: { value: 5.41, unit: "mg" },
    zinc: { value: 7.08, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 454.4, unit: "mg" },
  },
},

// 22. Fesa (Gangetic hairfin anchovy)
{
  id: "fesa",
  local_name: "ফেস্যা",
  english_name: "Gangetic hairfin anchovy",
  aliases: ["Phassa", "Phaha", "Pahissa", "ফেস্যা", "ফাহা", "ফাইস্যা"],
  scientific_name: "Setipinna phasa",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/fesa.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.11, unit: "g" },
    fat: { value: 3, unit: "g" },
    energy: { value: 441.7, unit: "kj" },
    calcium: { value: 3.10, unit: "mg" },
    iron: { value: 13.4, unit: "mg" },
    zinc: { value: 9.36, unit: "mg" },
    vitaminA: { value: 12, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 377.77, unit: "mg" },
  },
},

// 23. Gaberdana (Spined anchovy)
{
  id: "gaberdana",
  local_name: "গাবেরদানা",
  english_name: "Spined anchovy",
  aliases: ["Gaberdana",  "Gaberati", "Hicchori", "গাবেরদানা", "গাবের আটি",	"হিচ্চরি" ],
  scientific_name: "Stolephorus tri",
  portion_types: ["portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/gaberdana.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 14.18, unit: "g" },
    fat: { value: 1.09, unit: "g" },
    energy: { value: 356.96, unit: "kj" },
    calcium: { value: 5.94, unit: "mg" },
    iron: { value: 13.32, unit: "mg" },
    zinc: { value: 16.72, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 226.71, unit: "mg" },
  },
},

// 24. Ilish (Hilsa shad)
{
  id: "ilish",
  local_name: "ইলিশ",
  english_name: "Hilsa",
  aliases: ["Ilish", "Ilsha", "ইলিশ", "ইলশা"],
  scientific_name: "Tenualosa ilisha",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "smoked", "grilled"],
  has_soft_bone: true,
  image_url: "/assets/fish/hilsha.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 15.11, unit: "g" },
    fat: { value: 18.28, unit: "g" },
    energy: { value: 1010.13, unit: "kj" },
    calcium: { value: 1.96, unit: "mg" },
    iron: { value: 8.57, unit: "mg" },
    zinc: { value: 5.5, unit: "mg" },
    vitaminA: { value: 20, unit: "µg RAE" },
    vitaminD: { value: 18, unit: "µg" },
    omega3: { value: 1305.73, unit: "mg" },
  },
},

// 25. Katkoi (Crescent perch)
{
  id: "katkoi",
  local_name: "কাট কৈ",
  english_name: "Crescent perch",
  aliases: ["Katkoi", "কাট কৈ"],
  scientific_name: "Terapon jarbua",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/katkoi.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.57, unit: "g" },
    fat: { value: 4.79, unit: "g" },
    energy: { value: 767.89, unit: "kj" },
    calcium: { value: 0.50, unit: "mg" },
    iron: { value: 1.2, unit: "mg" },
    zinc: { value: 2.7, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 647.43, unit: "mg" },
  },
},

// 26. Khoira (Gizzard shad)
{
  id: "khoira",
  local_name: "খয়রা",
  english_name: "Gizzard shad",
  aliases: ["Khoira", "Barang", "খয়রা", "বরাং"],
  scientific_name: "Nematalosa nasus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: true,
  image_url: "/assets/fish/khoira.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.96, unit: "g" },
    fat: { value: 1.07, unit: "g" },
    energy: { value: 892.25, unit: "kj" },
    calcium: { value: 7.49, unit: "mg" },
    iron: { value: 10.44, unit: "mg" },
    zinc: { value: 14.48, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 147.26, unit: "mg" },
  },
},

// 27. Khorshula (Mullet)
{
  id: "khorshula",
  local_name: "খরশুলা",
  english_name: "Mullet",
  aliases: ["Khorshula", "Bata", "Khalla", "Arwari", "Halla", "Hira", "Khor", "Pungtara", "Urul", "খরশুলা", "বাটা", "খাল্লা", "আরওয়ারি", "হাল্লা", "হিরা", "খোর", "পুংতারা", "উরুল"],
  scientific_name: "Rhinomugil corsula",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/khorshula.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.09, unit: "g" },
    fat: { value: 3.27, unit: "g" },
    energy: { value: 501.2, unit: "kj" },
    calcium: { value: 5.59, unit: "mg" },
    iron: { value: 9.34, unit: "mg" },
    zinc: { value: 19.37, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 403.92, unit: "mg" },
  },
},

// 28. Nona Tengra (Long whiskers catfish)
{
  id: "nona_tengra",
  local_name: "নোনা টেংরা",
  english_name: "Long whiskers catfish",
  aliases: ["Nona Tengra", "Guillya", "নোনা টেংরা", "গুইল্লা"],
  scientific_name: "Mystus gulio",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed"],
  has_soft_bone: true,
  image_url: "/assets/fish/nona_tengra.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.88, unit: "g" },
    fat: { value: 5.11, unit: "g" },
    energy: { value: 539.5, unit: "kj" },
    calcium: { value: 6.37, unit: "mg" },
    iron: { value: 41.22, unit: "mg" },
    zinc: { value: 17.59, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 355.63, unit: "mg" },
  },
},

// 29. Parsey (Parse)
{
  id: "parsey",
  local_name: "পারসে",
  english_name: "Parse",
  aliases: ["Parsey", "পারসে"],
  scientific_name: "Liza subviridis",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/parsey.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.51, unit: "g" },
    fat: { value: 7.42, unit: "g" },
    energy: { value: 969.39, unit: "kj" },
    calcium: { value: 1.04, unit: "mg" },
    iron: { value: 11.98, unit: "mg" },
    zinc: { value: 7.79, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 598, unit: "mg" },
  },
},

// 30. Payra/Chitra (Spotted scat)
{
  id: "payra",
  local_name: "পায়রা",
  english_name: "Spotted scat",
  aliases: ["Payra", "Chitra", "Bishtara", "Chitrachanda", "পায়রা", "চিত্রা", "বিশতারা", "চিত্রাচান্দা"],
  scientific_name: "Scatophagus argus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/payra.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.48, unit: "g" },
    fat: { value: 5.81, unit: "g" },
    energy: { value: 581.7, unit: "kj" },
    calcium: { value: 16.97, unit: "mg" },
    iron: { value: 10.01, unit: "mg" },
    zinc: { value: 15.3, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 493.4, unit: "mg" },
  },
},

// 31. Shilong (Silond catfish)
{
  id: "shilong",
  local_name: "শিলং",
  english_name: "Silond catfish",
  aliases: ["Shilong", "Silond tengra", "শিলং", "সিলন্ড টেংরা"],
  scientific_name: "Silonia silondia",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry"],
  has_soft_bone: false,
  image_url: "/assets/fish/shilong.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.36, unit: "g" },
    fat: { value: 2.78, unit: "g" },
    energy: { value: 610.05, unit: "kj" },
    calcium: { value: 0.0001, unit: "mg" },
    iron: { value: 0.01, unit: "mg" },
    zinc: { value: 0.08, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 190.32, unit: "mg" },
  },
},

// 32. Taposhi (Paradise Threadfin)
{
  id: "taposhi",
  local_name: "তাপসী",
  english_name: "Paradise Threadfin",
  aliases: ["Taposhi", "Ramsos", "Rickshaw", "Muni", "Rishi", "তাপসী", "রামছোস", "রিক্সা", "মুনি", "ঋষি"],
  scientific_name: "Polynemus paradiseus",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/taposhi.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.35, unit: "g" },
    fat: { value: 1.78, unit: "g" },
    energy: { value: 655.42, unit: "kj" },
    calcium: { value: 0.97, unit: "mg" },
    iron: { value: 2.91, unit: "mg" },
    zinc: { value: 3.15, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 153.47, unit: "mg" },
  },
},

// 33. Vetki/Koral (Barramundi/Sea bass)
{
  id: "koral",
  local_name: "কোরাল",
  english_name: "Sea Bass",
  aliases: ["Barramundi", "Koral", "Vetki", "Patari", "কোরাল", "ভেটকি", "পাতারী"],
  scientific_name: "Lates calcarifer",
  portion_types: ["whole", "piece"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/koral.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 20.63, unit: "g" },
    fat: { value: 4, unit: "g" },
    energy: { value: 920.52, unit: "kj" },
    calcium: { value: 2.06, unit: "mg" },
    iron: { value: 23.88, unit: "mg" },
    zinc: { value: 6.93, unit: "mg" },
    vitaminA: { value: 8, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 496.38, unit: "mg" },
  },
},

// ========== SHRIMP, PRAWN & CRAB (34-42) ==========

// 34. Bagda (Black tiger shrimp)
{
  id: "bagda",
  local_name: "বাগদা চিংড়ি",
  english_name: "Black tiger shrimp",
  aliases: ["Bagda Chingri", "বাগদা চিংড়ি"],
  scientific_name: "Penaeus monodon",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/bagda.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 20.39, unit: "g" },
    fat: { value: 0.92, unit: "g" },
    energy: { value: 664.07, unit: "kj" },
    calcium: { value: 1.04, unit: "mg" },
    iron: { value: 3.67, unit: "mg" },
    zinc: { value: 13.66, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 85.5, unit: "mg" },
  },
},

// 35. Chaka (Indian prawn)
{
  id: "chaka",
  local_name: "চাকা চিংড়ি",
  english_name: "Indian prawn",
  aliases: ["Chaka Chingri", "চাকা চিংড়ি"],
  scientific_name: "Penaeus indicus",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/chaka.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.86, unit: "g" },
    fat: { value: 1.16, unit: "g" },
    energy: { value: 475.66, unit: "kj" },
    calcium: { value: 0.60, unit: "mg" },
    iron: { value: 15.94, unit: "mg" },
    zinc: { value: 11.94, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 108.61, unit: "mg" },
  },
},

// 36. Chali (Yellow shrimp)
{
  id: "chali",
  local_name: "চালি চিংড়ি",
  english_name: "Yellow shrimp",
  aliases: ["Chali Chingri", "চালি চিংড়ি"],
  scientific_name: "Metapenaeus brevicornis",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/chali.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.63, unit: "g" },
    fat: { value: 1.36, unit: "g" },
    energy: { value: 451.59, unit: "kj" },
    calcium: { value: 0.63, unit: "mg" },
    iron: { value: 9.44, unit: "mg" },
    zinc: { value: 9.96, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 136, unit: "mg" },
  },
},

// 37. Goda (Goda River prawn)
{
  id: "goda",
  local_name: "গদা চিংড়ি",
  english_name: "Goda River prawn",
  aliases: ["Goda Chingri", "গদা চিংড়ি"],
  scientific_name: "Macrobrachium scabriculum",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/goda.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 17.89, unit: "g" },
    fat: { value: 1.15, unit: "g" },
    energy: { value: 460.32, unit: "kj" },
    calcium: { value: 0.76, unit: "mg" },
    iron: { value: 13.99, unit: "mg" },
    zinc: { value: 13.92, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 94.9, unit: "mg" },
  },
},

// 38. Golda (Giant freshwater prawn)
{
  id: "prawn",
  local_name: "গলদা চিংড়ি",
  english_name: "Giant freshwater prawn",
  aliases: ["Golda Chingri", "গলদা চিংড়ি"],
  scientific_name: "Macrobrachium rosenbergii",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/prawn.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 19.44, unit: "g" },
    fat: { value: 1.15, unit: "g" },
    energy: { value: 665.06, unit: "kj" },
    calcium: { value: 0.83, unit: "mg" },
    iron: { value: 8.68, unit: "mg" },
    zinc: { value: 15.5, unit: "mg" },
    vitaminA: { value: 2, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 72.04, unit: "mg" },
  },
},

// 39. Horina (Speckled shrimp)
{
  id: "horina",
  local_name: "হরিনা চিংড়ি",
  english_name: "Speckled shrimp",
  aliases: ["Horina Chingri", "Khorkhore chingri", "হরিনা চিংড়ি", "খরখরে চিংড়ি"],
  scientific_name: "Metapenaeus monoceros",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/horina.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 18.83, unit: "g" },
    fat: { value: 1, unit: "g" },
    energy: { value: 430.73, unit: "kj" },
    calcium: { value: 0.95, unit: "mg" },
    iron: { value: 11.55, unit: "mg" },
    zinc: { value: 18.54, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 0.055, unit: "µg" },
    omega3: { value: 79.38, unit: "mg" },
  },
},

// 40. Kakra (Mud crab)
{
  id: "kakra",
  local_name: "কাকড়া",
  english_name: "Mud crab",
  aliases: ["Kakra", "কাকড়া"],
  scientific_name: "Scylla serrata",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/kakra.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 15.2, unit: "g" },
    fat: { value: 2.57, unit: "g" },
    energy: { value: 366.85, unit: "kj" },
    calcium: { value: 8.38, unit: "mg" },
    iron: { value: 112.17, unit: "mg" },
    zinc: { value: 51.11, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 257.91, unit: "mg" },
  },
},

// 41. Rosna (Roshna prawn)
{
  id: "rosna",
  local_name: "রসনা চিংড়ি",
  english_name: "Roshna prawn",
  aliases: ["Rosna Chingri", "Balam Chingri", "রসনা চিংড়ি", "বালাম চিংড়ি"],
  scientific_name: "Exopalaemon styliferus",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "mashed", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/rosna.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.78, unit: "g" },
    fat: { value: 1.17, unit: "g" },
    energy: { value: 451.94, unit: "kj" },
    calcium: { value: 1.83, unit: "mg" },
    iron: { value: 8.63, unit: "mg" },
    zinc: { value: 11.97, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 127.97, unit: "mg" },
  },
},

// 42. Tiger/Shrimp (Green tiger prawn)
{
  id: "tiger_prawn",
  local_name: "টাইগার চিংড়ি",
  english_name: "Green tiger prawn",
  aliases: ["Tiger Chingri", "টাইগার চিংড়ি"],
  scientific_name: "Penaeus semisulcatus",
  portion_types: ["whole", "portion"],
  recipes: ["fry", "curry", "grilled"],
  has_soft_bone: false,
  image_url: "/assets/fish/tiger_prawn.webp",
  nutrients_per_100g_fallback: {
    protein: { value: 16.42, unit: "g" },
    fat: { value: 0.73, unit: "g" },
    energy: { value: 538.41, unit: "kj" },
    calcium: { value: 0.64, unit: "mg" },
    iron: { value: 9.2, unit: "mg" },
    zinc: { value: 9.61, unit: "mg" },
    vitaminA: { value: 1, unit: "µg RAE" },
    vitaminD: { value: 1, unit: "µg" },
    omega3: { value: 102.66, unit: "mg" },
  },
},

];